import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react"
import AnimatedTextLoader from "./AnimatedTextLoader"
import Loader from "../../../../components/ui/loader"
import PayPalCardFields from "../../../../components/PayPal/Private/PayPalCardFields"
import { Button } from "../../../../components/ui/button"
import {
  PaymentRequests,
  SubscriptionRequests,
} from "../../../../api/app.service"
import { PayPalButton } from "../../../../components/PayPal/Private/PayPalButton"
import {
  SvgAmericanExpress,
  SvgDiscover,
  SvgMastercard,
  SvgVisa,
  SvgDinersClub,
  SvgPlus,
  SvgChevronLeft,
} from "../../../../components/icons"
import { UserCtx } from "../../../../context/userContext"
import { useLocation, useNavigate } from "react-router-dom"
import { useToast } from "../../../../hooks/useToast"

const UpdatePaymentPage: React.FC = () => {
  const { state } = useLocation()
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] =
    useState<boolean>(false)
  const [isChecked, setIsChecked] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(false)
  const [isSdkReady, setIsSdkReady] = useState(false)
  const [loadingUpdate, setLoadingUpdate] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const isCheckedRef = useRef(false)
  const transaction = state?.transaction
  const payments = state?.localPaymentMethods
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID
  const merchantID = process.env.REACT_APP_PAYPAL_PAYER_ID
  const navigate = useNavigate()

  const createVaultSetupToken = useCallback(
    (data: any, type: "paypal" | "card" | "apple_pay" | "google-pay") => {
      return PaymentRequests.createPaymentToken({
        user_id: parseInt(user.user_id),
        type: type,
      })
        .then((response: any) => {
          return response.data
        })
        .then((order: any) => {
          return order.id
        })
    },
    []
  )

  const getPaymentMethods = async () => {
    if (!user) return
    setLoadingPaymentMethods(true)
    try {
      const data = await PaymentRequests.getSavedPaymentMethods({
        user_id: parseInt(user.user_id),
      })

      const allCards = (Array.isArray(data?.data) ? data.data : [])
        .flatMap((entry: any) => entry?.payment_tokens || [])
        .filter((token: any) => token?.payment_source?.card)
        .map((token: any) => ({
          id: token.id,
          payment_method_token: token.id,
          ...token.payment_source.card,
        }))

      setPaymentMethods(allCards)
    } catch (ex: any) {
      console.error("Error fetching payment methods:", ex)
      toast.error(
        ex.response?.data?.message || "Failed to load payment methods."
      )
    } finally {
      setLoadingPaymentMethods(false)
    }
  }

  const onApprove = async (tokenData: any): Promise<void> => {
    setLoadingUpdate(true)
    try {
      const saveResponse = await PaymentRequests.savePaymentToken({
        user_id: parseInt(user.user_id),
        token: tokenData,
        is_update: true,
      })

      const payment_token_id = saveResponse?.data?.payment_method
      if (!payment_token_id) {
        throw new Error("No payment token ID returned from PayPal.")
      }

      if (transaction?.description === "Subscription Payment") {
        await SubscriptionRequests.updateSubscriptionPaymentMethod({
          user_id: parseInt(user.user_id),
          payment_token_id,
        })
      } else {
        await PaymentRequests.updatePaymentMethod({
          user_id: parseInt(user.user_id),
          service_payment_id: transaction?.service_payment_id,
          payment_token_id,
        })
      }

      toast.success("Payment method updated successfully!")
      navigate("/account-settings/payment-center", {
        state: { localPaymentMethods: payments || paymentMethods },
      })
    } catch (err: any) {
      console.error("Error in PayPal onApprove flow:", err)
      toast.error(
        err.response?.data?.error_detail ||
          "Failed to save or update your payment method. Please try again."
      )
    } finally {
      setLoadingUpdate(false)
    }
  }

  const updatePaymentMethod = async (payment_token_id: string) => {
    try {
      if (transaction?.description === "Subscription Payment") {
        await SubscriptionRequests.updateSubscriptionPaymentMethod({
          user_id: parseInt(user.user_id),
          payment_token_id,
        })
      } else {
        await PaymentRequests.updatePaymentMethod({
          user_id: parseInt(user.user_id),
          service_payment_id: transaction?.service_payment_id,
          payment_token_id,
        })
      }

      toast.success("Payment method updated successfully!")
      navigate("/account-settings/payment-center", {
        state: { localPaymentMethods: payments || paymentMethods },
      })
    } catch (err: any) {
      console.error("Error updating payment method:", err)
      toast.error(
        err.response?.data?.error_detail ||
          "Failed to update your payment method. Please contact support."
      )
      throw err
    }
  }

  const handleSave = async () => {
    setLoadingUpdate(true)
    if (!selectedCardId) {
      toast.error("Please select a payment method.")
      setLoadingUpdate(false)
      return
    }
    try {
      await updatePaymentMethod(selectedCardId)
    } catch (err) {
      console.error("Error while saving:", err)
      setLoadingUpdate(false)
    }
  }

  useEffect(() => {
    if (!payments || payments.length === 0) {
      getPaymentMethods()
    }
  }, [user, payments])

  useEffect(() => {
    const loadPayPalSDK = () => {
      if (document.getElementById("paypal-sdk")) {
        console.log("PayPal SDK already loaded.")
        setIsSdkReady(true)
        return
      }

      const script = document.createElement("script")
      script.id = "paypal-sdk"
      script.src = `https://www.paypal.com/sdk/js?components=buttons,card-fields,applepay&client-id=${clientID}&merchant-id=${merchantID}&currency=USD&intent=capture&disable-funding=paylater`
      script.async = true

      script.onload = () => {
        console.log("PayPal SDK loaded successfully.")
        setIsSdkReady(true)
      }

      script.onerror = (err) => {
        console.error("Failed to load PayPal SDK:", err)
      }

      document.body.appendChild(script)
    }

    loadPayPalSDK()
  }, [])

  if (!isSdkReady) {
    return (
      <div className="flex justify-center items-center w-full h-[300px]">
        <Loader />
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col items-center py-10 px-4 md:px-8 -mt-12 min-h-screen">
      <div className="relative w-full max-w-[640px] bg-white rounded-[16px] shadow-md flex flex-col items-center overflow-y-auto">
        <div className="w-full pt-[60px] px-[24px] md:px-[48px] text-center">
          <h1 className="text-black text-[22px] md:text-[26px] font-semibold mb-[8px]">
            Update Payment Method
          </h1>
          <p className="text-grayCharcoal text-[14px] mb-[20px] leading-snug max-w-[520px] mx-auto">
            You’re updating payment for{" "}
            <strong>{transaction?.service_name}</strong> — amount:{" "}
            <strong>${parseFloat(transaction?.amount || 0).toFixed(2)}</strong>.
          </p>
        </div>

        <div className="w-full flex flex-col items-center mb-6">
          {loadingPaymentMethods && (!payments || payments.length === 0) ? (
            <div className="flex justify-center items-center h-[200px] w-full">
              <AnimatedTextLoader text="Loading your payment options..." />{" "}
            </div>
          ) : (
            <>
              <div className="flex justify-center border-b border-grayMist p-2 mb-4 w-full max-w-[440px] bg-transparent">
                <h3 className="text-[16px] font-semibold text-center w-full">
                  Your Saved Payment Methods
                </h3>
              </div>

              <div className="w-full max-w-[440px] flex flex-col gap-2">
                {(payments && payments.length > 0 ? payments : paymentMethods)
                  .length > 0 ? (
                  (payments && payments.length > 0
                    ? payments
                    : paymentMethods
                  ).map((card: any) => (
                    <label
                      key={card.id}
                      htmlFor={`card-${card.id}`}
                      className={`grid grid-cols-[auto_1fr_2fr_1fr] items-center border rounded-lg px-3 py-2 shadow-sm text-[14px] cursor-pointer transition-all duration-150 ${
                        selectedCardId === card.id
                          ? "border-blurple bg-indigo-50"
                          : "border-grayMist bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <input
                        id={`card-${card.id}`}
                        type="radio"
                        name="selectedCard"
                        checked={selectedCardId === card.id}
                        onChange={() => setSelectedCardId(card.id)}
                        className="w-4 h-4 text-blurple accent-blurple cursor-pointer"
                        disabled={loadingUpdate}
                      />

                      <span className="font-medium text-gray-800 truncate ml-2">
                        {card.name || "—"}
                      </span>

                      <div className="flex items-center justify-center gap-2 text-gray-700">
                        {(() => {
                          const brand = card.brand?.toLowerCase()
                          const Icon =
                            brand === "visa"
                              ? SvgVisa
                              : brand === "mastercard"
                              ? SvgMastercard
                              : brand === "american express" || brand === "amex"
                              ? SvgAmericanExpress
                              : brand === "discover"
                              ? SvgDiscover
                              : brand === "dinersclub"
                              ? SvgDinersClub
                              : null
                          return Icon ? (
                            <Icon className="w-5 h-5 shrink-0" />
                          ) : null
                        })()}
                        <span>•••• {card.last_digits}</span>
                      </div>

                      <span className="text-center text-gray-600">
                        {card.expiry ? card.expiry.replace("-", "/") : "—"}
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="text-center text-graySlate text-[14px] py-10">
                    No saved payment methods found.
                  </div>
                )}

                <label
                  htmlFor="card-new"
                  className={`flex items-center justify-between border rounded-lg px-3 py-3 shadow-sm cursor-pointer transition-all duration-150 ${
                    selectedCardId === "new"
                      ? "border-blurple bg-indigo-50"
                      : "border-grayMist bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      id="card-new"
                      type="radio"
                      name="selectedCard"
                      checked={selectedCardId === "new"}
                      onChange={() => setSelectedCardId("new")}
                      className="w-4 h-4 text-blurple accent-blurple cursor-pointer"
                      disabled={loadingUpdate}
                    />
                    <span className="font-medium text-gray-800">
                      Add New Payment Method
                    </span>
                  </div>
                  <span className="text-gray-500 text-sm">
                    <SvgPlus />
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        {selectedCardId === "new" ? (
          <div className="w-full flex flex-col items-center text-center rounded-lg p-4 -mt-2">
            <div className="w-full max-w-[440px] mb-6">
              <div className="justify-flex flex-col items-center justify-center">
                <PayPalButton
                  createVaultSetupToken={createVaultSetupToken}
                  onApprove={onApprove}
                />
              </div>
              <div>
                {/* <PayPalApplePay
                  createVaultSetupToken={createVaultSetupToken}
                  onApprove={onApprove}
                  isSdkReady={isSdkReady}
                  reactiveOrderTotal={parseFloat(transaction.amount)}
                /> */}
              </div>
              <div className="relative flex items-center justify-center w-full my-4 -mb-4">
                <div className="flex-grow border-t border-grayMist"></div>
                <span className="px-3 text-graySlate text-sm">OR</span>
                <div className="flex-grow border-t border-grayMist"></div>
              </div>
            </div>

            <div className="w-full max-w-[420px]">
              <div className="mb-[12px] flex flex-col items-center">
                <img
                  src="https://www.paypalobjects.com/webstatic/mktg/logo/AM_mc_vs_dc_ae.jpg"
                  alt="Payments by PayPal"
                  className="h-10 w-auto"
                />
                <p className="text-grayCharcoal text-[12px] mt-1 leading-tight">
                  Secured by PayPal — your card details stay private.
                </p>
              </div>

              <p className="text-[13px] mb-[16px] max-w-[360px] leading-snug mx-auto">
                Please enter the cardholder name and card details below.
              </p>

              <PayPalCardFields
                createVaultSetupToken={createVaultSetupToken}
                onApprove={onApprove}
                isCheckedRef={isCheckedRef}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                isTermsAgreed={isTermsAgreed}
                setIsTermsAgreed={setIsTermsAgreed}
                isSubx={false}
                activeService=""
                onCancel={() =>
                  navigate("/account-settings/payment-center", {
                    state: { localPaymentMethods: payments || paymentMethods },
                  })
                }
              />
            </div>
          </div>
        ) : (
          <div className="w-full gap-4 flex justify-center mb-8">
            <Button className="w-1/4" onClick={handleSave}>
              {loadingUpdate ? <Loader /> : "Save"}
            </Button>

            <Button
              className="w-1/4"
              onClick={() =>
                navigate("/account-settings/payment-center", {
                  state: { localPaymentMethods: payments || paymentMethods },
                })
              }
              variant="outline"
            >
              {loadingUpdate ? <Loader /> : "Cancel"}
            </Button>
          </div>
        )}

        <p className="text-graySlate text-[12px] text-center mt-[16px] leading-tight max-w-[460px] mb-8">
          By saving this payment method, you agree to Zoee’s{" "}
          <a
            href="/terms-and-conditions"
            target="_blank"
            className="text-blurple"
          >
            Agreement & Disclosures
          </a>
          , our{" "}
          <a
            href="https://intercom.help/zoee/en/articles/8623117-how-does-zoee-protect-your-information-on-paypal"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blurple"
          >
            Payment Authorization
          </a>{" "}
          terms, and PayPal’s{" "}
          <a
            href="https://www.paypal.com/us/webapps/mpp/ua/useragreement-full"
            target="_blank"
            className="text-blurple"
          >
            User Agreement
          </a>
        </p>
      </div>
    </div>
  )
}

export default UpdatePaymentPage
